import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// Criterios de la política de contraseña (alineados con la regex de validación
// front/back: 8+, mayúscula, minúscula, número y carácter especial).
const CRITERIA = [
  { key: 'length', test: (v) => v.length >= 8 },
  { key: 'uppercase', test: (v) => /[A-Z]/.test(v) },
  { key: 'lowercase', test: (v) => /[a-z]/.test(v) },
  { key: 'number', test: (v) => /[0-9]/.test(v) },
  { key: 'symbol', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

// Niveles de fuerza: grises neutros que culminan en dorado (un solo acento). Todos los
// colores son TOKENS del tema (mode-aware) para que se lean bien también en claro:
// `gold.main` es ámbar profundo en claro y dorado brillante en oscuro.
const LEVELS = [
  { min: 0, key: 'weak', color: 'text.disabled' },
  { min: 2, key: 'fair', color: 'text.secondary' },
  { min: 4, key: 'good', color: 'gold.main' },
  { min: 5, key: 'strong', color: 'gold.main' },
];

/**
 * Checklist en vivo de criterios de contraseña + barra de fuerza con puntuación.
 * Aparece solo mientras el campo tiene foco o ya hay texto (controlado por `visible`).
 * Se usa en registro y en restablecer contraseña (donde se CREA una contraseña).
 * Premium + legible en claro y oscuro: panel sobre superficie elevada con hairline,
 * texto en `text.primary`/`text.secondary` y acento dorado del modo.
 */
function PasswordStrengthHints({ value = '', visible = false }) {
  const { t } = useTranslation(['validations']);
  const password = value || '';

  const results = CRITERIA.map((c) => ({ ...c, passed: c.test(password) }));
  const score = results.filter((r) => r.passed).length;
  const level = [...LEVELS].reverse().find((l) => score >= l.min) || LEVELS[0];

  return (
    <Collapse in={visible} unmountOnExit>
      <Box
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'hairline.strong',
          backgroundColor: 'surface.content',
          boxShadow: 'var(--otx-card-shadow)',
        }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1.25}>
          <LinearProgress
            variant="determinate"
            value={(score / CRITERIA.length) * 100}
            aria-label={t(`passwordHints.strength.${level.key}`)}
            sx={(theme) => ({
              flex: 1,
              height: 6,
              borderRadius: 3,
              // Pista neutra mode-aware (antes era blanca translúcida → invisible en claro).
              backgroundColor:
                theme.palette.mode === 'light' ? 'rgba(17,21,27,0.10)' : 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: level.color,
                transition:
                  'transform 250ms cubic-bezier(0.32,0.72,0,1), background-color 250ms cubic-bezier(0.32,0.72,0,1)',
              },
            })}
          />
          <Typography
            variant="caption"
            sx={{ color: level.color, fontWeight: 800, whiteSpace: 'nowrap' }}>
            {t(`passwordHints.strength.${level.key}`)}
          </Typography>
        </Stack>
        <Stack spacing={0.5}>
          {results.map((r) => (
            <Stack key={r.key} direction="row" spacing={0.75} alignItems="center">
              {r.passed ? (
                <CheckCircleIcon sx={{ fontSize: 14, color: 'gold.main' }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: r.passed ? 'text.primary' : 'text.secondary',
                  fontWeight: r.passed ? 600 : 400,
                }}>
                {t(`passwordHints.${r.key}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Collapse>
  );
}

PasswordStrengthHints.propTypes = {
  value: PropTypes.string,
  visible: PropTypes.bool,
};

export default PasswordStrengthHints;
